import React, { useState } from "react";

const FormPreview = ({ form }) => {
  const [formData, setFormData] = useState({});
  const [submitted, setSubmitted] = useState(false);

  if (!form || !form.fields || form.fields.length === 0) {
    return (
      <div className="preview-empty">
        <p>No fields to preview. Add some fields first!</p>
      </div>
    );
  }

  const handleChange = (fieldId, value) => {
    setFormData({ ...formData, [fieldId]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const handleDownloadPDF = () => {
    // Create a printable window with the form
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${form.title}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;700;800&display=swap');

            * { margin: 0; padding: 0; box-sizing: border-box; }

            body {
              font-family: 'Plus Jakarta Sans', Arial, sans-serif;
              background: white;
              color: #1a1a2e;
              padding: 40px;
            }

            .form-header {
              border-bottom: 3px solid #7c6ef5;
              padding-bottom: 20px;
              margin-bottom: 30px;
            }

            .form-title {
              font-size: 24px;
              font-weight: 800;
              color: #1a1a2e;
              margin-bottom: 6px;
              letter-spacing: -0.5px;
            }

            .form-desc {
              font-size: 13px;
              color: #8b8fa8;
            }

            .field-group {
              margin-bottom: 22px;
            }

            .field-label {
              display: block;
              font-size: 13px;
              font-weight: 700;
              margin-bottom: 8px;
              color: #1a1a2e;
            }

            .required-star { color: #f04f4f; }

            .field-input {
              width: 100%;
              padding: 10px 14px;
              border: 1.5px solid #e8eaf0;
              border-radius: 8px;
              font-size: 13px;
              color: #1a1a2e;
              background: #f5f6fa;
              font-family: inherit;
            }

            .field-input-tall {
              height: 80px;
            }

            .option-item {
              display: flex;
              align-items: center;
              gap: 10px;
              margin-bottom: 8px;
              font-size: 13px;
            }

            .option-circle {
              width: 16px; height: 16px;
              border: 2px solid #e8eaf0;
              border-radius: 50%;
              flex-shrink: 0;
            }

            .option-square {
              width: 16px; height: 16px;
              border: 2px solid #e8eaf0;
              border-radius: 3px;
              flex-shrink: 0;
            }

            .footer {
              margin-top: 40px;
              padding-top: 16px;
              border-top: 1px solid #e8eaf0;
              font-size: 11px;
              color: #8b8fa8;
              text-align: center;
            }

            @media print {
              body { padding: 20px; }
            }
          </style>
        </head>
        <body>
          <div class="form-header">
            <div class="form-title">${form.title}</div>
            ${form.description ? `<div class="form-desc">${form.description}</div>` : ""}
          </div>

          ${form.fields.map((field) => `
            <div class="field-group">
              <label class="field-label">
                ${field.label}
                ${field.required ? '<span class="required-star"> *</span>' : ""}
              </label>

              ${field.type === "textarea"
                ? `<div class="field-input field-input-tall"></div>`
                : field.type === "select"
                ? `<div class="field-input">▾ Select an option</div>`
                : field.type === "radio"
                ? (field.options || []).map(opt => `
                    <div class="option-item">
                      <div class="option-circle"></div>
                      <span>${opt}</span>
                    </div>`).join("")
                : field.type === "checkbox"
                ? (field.options || []).map(opt => `
                    <div class="option-item">
                      <div class="option-square"></div>
                      <span>${opt}</span>
                    </div>`).join("")
                : `<div class="field-input">${field.placeholder || ""}</div>`
              }
            </div>
          `).join("")}

          <div class="footer">
            Generated by FormCraft • ${new Date().toLocaleDateString()}
          </div>

          <script>
            window.onload = function() {
              window.print();
            }
          </script>
        </body>
      </html>
    `;

    const printWindow = window.open("", "_blank", "width=800,height=900");
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  if (submitted) {
    return (
      <div className="preview-success">
        <div className="success-icon">✅</div>
        <h3>Form Submitted!</h3>
        <p>Thank you for your response.</p>
        <button onClick={() => setSubmitted(false)} className="btn-reset">
          Submit Again
        </button>
      </div>
    );
  }

  const renderField = (field) => {
    const value = formData[field.id] || "";

    switch (field.type) {
      case "textarea":
        return (
          <textarea
            value={value}
            onChange={(e) => handleChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            rows={4}
          />
        );
      case "select":
        return (
          <select
            value={value}
            onChange={(e) => handleChange(field.id, e.target.value)}
            required={field.required}
          >
            <option value="">-- Select an option --</option>
            {(field.options || []).map((opt, i) => (
              <option key={i} value={opt}>{opt}</option>
            ))}
          </select>
        );
      case "radio":
        return (
          <div className="radio-group">
            {(field.options || []).map((opt, i) => (
              <label key={i} className="radio-label">
                <input
                  type="radio"
                  name={field.id}
                  value={opt}
                  checked={value === opt}
                  onChange={(e) => handleChange(field.id, e.target.value)}
                  required={field.required}
                />
                {opt}
              </label>
            ))}
          </div>
        );
      case "checkbox":
        return (
          <div className="checkbox-group-preview">
            {(field.options || []).map((opt, i) => {
              const checked = Array.isArray(value) && value.includes(opt);
              return (
                <label key={i} className="checkbox-label">
                  <input
                    type="checkbox"
                    value={opt}
                    checked={checked}
                    onChange={(e) => {
                      const current = Array.isArray(value) ? value : [];
                      const updated = e.target.checked
                        ? [...current, opt]
                        : current.filter((v) => v !== opt);
                      handleChange(field.id, updated);
                    }}
                  />
                  {opt}
                </label>
              );
            })}
          </div>
        );
      default:
        return (
          <input
            type={field.type}
            value={value}
            onChange={(e) => handleChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
          />
        );
    }
  };

  return (
    <div className="form-preview">
      <div className="preview-header">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h2>{form.title}</h2>
            {form.description && <p className="preview-desc">{form.description}</p>}
          </div>
          {/* Download PDF Button */}
          <button
            onClick={handleDownloadPDF}
            style={{
              background: "#7c6ef5",
              color: "white",
              border: "none",
              padding: "9px 18px",
              borderRadius: "8px",
              fontSize: "0.82rem",
              fontWeight: "700",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontFamily: "inherit",
              whiteSpace: "nowrap",
              flexShrink: 0,
              marginLeft: "16px",
            }}
          >
            ⬇ Download PDF
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="preview-form">
        {form.fields.map((field) => (
          <div key={field.id} className="preview-field">
            <label className="preview-label">
              {field.label}
              {field.required && <span className="required-star"> *</span>}
            </label>
            {renderField(field)}
          </div>
        ))}
        <button type="submit" className="btn-submit">Submit Form</button>
      </form>
    </div>
  );
};

export default FormPreview;